import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  BarChart3,
  BellRing,
  Boxes,
  CalendarClock,
  CreditCard,
  FileText,
  History,
  LayoutDashboard,
  LogOut,
  Radar,
  ReceiptEuro,
  Repeat,
  Rocket,
  Settings,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import AiAssistant from "@/components/AiAssistant";
import InstallPrompt from "@/components/InstallPrompt";
import NotificationBell from "@/components/NotificationBell";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/preventivi", labelKey: "nav.quotes", icon: FileText, id: "nav-preventivi" },
  { to: "/fatture", labelKey: "nav.invoices", icon: ReceiptEuro, id: "nav-fatture" },
  { to: "/ricorrenti", labelKey: "nav.recurring", icon: Repeat, id: "nav-ricorrenti" },
  { to: "/solleciti", labelKey: "nav.reminders", icon: BellRing, id: "nav-solleciti" },
  { to: "/report", labelKey: "nav.report", icon: BarChart3, id: "nav-report" },
  { to: "/clienti", labelKey: "nav.clients", icon: Users, id: "nav-clienti" },
  { to: "/agenda", labelKey: "nav.agenda", icon: CalendarClock, id: "nav-agenda" },
  { to: "/memoria", labelKey: "nav.memory", icon: History, id: "nav-memoria" },
  { to: "/abbonamento", labelKey: "nav.plan", icon: CreditCard, id: "nav-abbonamento" },
  { to: "/impostazioni", labelKey: "nav.settings", icon: Settings, id: "nav-impostazioni" },
];

const ADMIN_NAV = [
  { to: "/lead-finder", labelKey: "nav.leadFinder", icon: Radar, id: "nav-lead-finder",
    admin: true },
  { to: "/lancio", labelKey: "nav.launch", icon: Rocket, id: "nav-lancio", admin: true },
  { to: "/admin", labelKey: "nav.admin", icon: ShieldCheck, id: "nav-admin", admin: true },
];

const AppLayout = () => {
  const { user, logout } = useAuth();
  const { t, lang } = useI18n();
  const [aiOpen, setAiOpen] = useState(false);
  const location = useLocation();
  const nav = user?.role === "admin" ? [...NAV, ...ADMIN_NAV] : NAV;
  const current = nav.find((n) => n.to === location.pathname);

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-slate-200 bg-white lg:flex">
        <Link to="/dashboard" className="block px-6 py-6" data-testid="brand-logo">
          <span className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-900">
              <Boxes className="h-4 w-4 text-white" />
            </span>
            <span className="text-lg font-extrabold tracking-tight">Operativo</span>
          </span>
          <span className="mt-1.5 block text-[11px] font-semibold leading-snug text-slate-500">
            {t("brand.tagline")}
          </span>
        </Link>
        <nav className="flex flex-1 flex-col gap-1 px-3">
          {nav.map(({ to, labelKey, icon: Icon, id }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={id}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {t(labelKey)}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-4">
          <LanguageSwitcher />
          <p className="mt-4 truncate text-sm font-semibold">{user?.name}</p>
          <p className="truncate text-xs text-slate-500">{user?.email}</p>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:text-red-600"
          >
            <LogOut className="h-3.5 w-3.5" /> {t("nav.logout")}
          </button>
        </div>
      </aside>

      <div className="lg:pl-60">
        <header className="glass-header sticky top-0 z-20 flex items-center justify-between px-5 py-4 lg:px-10">
          <div>
            <h1 className="text-xl font-extrabold tracking-tight lg:text-2xl">
              {current ? t(current.labelKey) : t("nav.document")}
            </h1>
            <p className="text-xs text-slate-500">
              {new Date().toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <Button
              data-testid="open-ai-assistant"
              onClick={() => setAiOpen(true)}
              className="gap-2 bg-indigo-600 text-white hover:bg-indigo-500"
            >
              <Sparkles className="h-4 w-4" /> {t("nav.aiAssistant")}
            </Button>
          </div>
        </header>

        <nav className="flex items-center gap-1 overflow-x-auto border-b border-slate-200 bg-white px-4 py-2 lg:hidden">
          {nav.map(({ to, labelKey, id }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${id}-mobile`}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600"
                }`
              }
            >
              {t(labelKey)}
            </NavLink>
          ))}
          <div className="ml-2 shrink-0">
            <LanguageSwitcher suffix="mobile" />
          </div>
        </nav>

        <main className="px-5 py-8 lg:px-10">
          <Outlet />
        </main>
      </div>

      <AiAssistant open={aiOpen} onClose={() => setAiOpen(false)} />
      <InstallPrompt />
    </div>
  );
};

export default AppLayout;
